import React, { useEffect, useRef, useState } from 'react'

import { Button, Col, Row } from '@ix/ix-ui'

import { AdvancedFilterType, FilterValueType, QuickFilterPresetType } from './RecordFilter.service'
import styled from 'styled-components'

type FilterPresetsProps = {
  presets: Array<QuickFilterPresetType>,
  activeFilters: {[x: string]: AdvancedFilterType},
  onPresetChange: (preset: AdvancedFilterType) => void,
}

const Seperator = styled.div`
    padding: 1rem;
    margin-left: -0.5em;
    font-weight: bolder;
    font-size: 2em;
    color: ${props => props.theme.colors.accentColor};
`

function QuickFilterPresets ({
  presets,
  activeFilters,
  onPresetChange,
} : FilterPresetsProps) {
  const [activeQuickPreset, setActiveQuickPreset] = useState<{[x: string]: boolean}>({})
  // Retains the preloaded allocated_user filter (with SPUD DB user id/name) for re-use when toggling ON
  const allocatedUserFilterRef = useRef<AdvancedFilterType | null>(null)
  useEffect(() => {
    if (presets) {
      let presetObj = {}

      presets.forEach(preset => {
        let filterKey = preset.name + '-0'

        // For status presets like 'site.status.pending', look for 'site.status-0' instead
        if (preset.name.startsWith('site.status.')) {
          filterKey = 'site.status-0'
        }

        const currentActivePreset = activeFilters?.[filterKey]
        // Save the preloaded "allocated to me" filter once.
        // It contains the current user's SPUD id and is reused when the filter is toggled back on.
        if (
          preset.name === 'allocated_user' &&
          currentActivePreset &&
          Array.isArray(currentActivePreset.value) &&
          (currentActivePreset.value as Array<unknown>).length > 0 &&
          !allocatedUserFilterRef.current
        ) {
          allocatedUserFilterRef.current = currentActivePreset
        }

        if (currentActivePreset && currentActivePreset.value) {
          // For status filters, check if the specific status is in the value array
          if (preset.name.startsWith('site.status.')) {
            const statusType = preset.name.split('.').pop()
            const isActive = Array.isArray(currentActivePreset.value) &&
              currentActivePreset.value.some((item: {id?: string}) => item.id === statusType)
            presetObj = { ...presetObj, [preset.name]: isActive, default_order: preset.default_order }
          } else if (preset.name === 'allocated_user') {
            // Highlight "Allocated to me" only when the logged-in user is the only selected user.
            // Turn it off if a different user or multiple users are selected in the advanced filter.
            const currentUserId = Array.isArray(allocatedUserFilterRef.current?.value) &&
              (allocatedUserFilterRef.current?.value as Array<{id: number}>)[0]?.id
            const isCurrentUser = Array.isArray(currentActivePreset.value) &&
              (currentActivePreset.value as Array<{id: number}>).length === 1 &&
              currentUserId != null &&
              (currentActivePreset.value as Array<{id: number}>)[0].id === currentUserId
            presetObj = { ...presetObj, [preset.name]: isCurrentUser, default_order: preset.default_order }
          } else {
            presetObj = { ...presetObj, [preset.name]: true, default_order: preset.default_order }
          }
        } else {
          presetObj = { ...presetObj, [preset.name]: false, default_order: preset.default_order }
        }
      })
      setActiveQuickPreset(presetObj)
    }
  }, [presets, activeFilters])

  return (
    <div>
      {presets.length > 0 &&
        <Row>
          <Col>
            <Row>
              {presets.filter(preset => preset.group === 'status')
                .map((button) => (
                  <Button
                    key={button.name}
                    active={activeQuickPreset[button.name]}
                    onClick={() => {
                      const newValue = !activeQuickPreset[button.name]
                      setActiveQuickPreset({ ...activeQuickPreset, [button.name]: newValue })

                      // Extract status type from preset name (e.g., 'site.status.pending' -> 'pending')
                      let filterName = button.name
                      let filterValue: FilterValueType = newValue

                      if (button.name.startsWith('site.status.')) {
                        filterName = 'site.status'
                        if (newValue) {
                          const statusType = button.name.split('.').pop()
                          filterValue = [{ id: statusType, name: button.label }]
                        } else {
                          filterValue = false // Use false to signal deletion
                        }
                      }

                      onPresetChange({
                        type: 'toggle',
                        label: button.label,
                        name: filterName,
                        value: filterValue,
                        defaultValue: [],
                        default_order: button.default_order,
                        role: 'any',
                      })
                    }}
                  >
                    {button.label}
                  </Button>
                ))}
              {presets.filter(preset => preset.group === 'allocation').length
                ? <Seperator>|</Seperator>
                : null
              }
              {presets.filter(preset => preset.group === 'allocation')
                .map((button) => (
                  <Button
                    key={button.name}
                    active={activeQuickPreset[button.name]}
                    onClick={() => {
                      const newValue = !activeQuickPreset[button.name]
                      setActiveQuickPreset({ ...activeQuickPreset, [button.name]: newValue })
                      if (button.name === 'allocated_user' && newValue && allocatedUserFilterRef.current) {
                        onPresetChange(allocatedUserFilterRef.current)
                      } else {
                        onPresetChange({
                          type: 'toggle',
                          label: button.label,
                          name: button.name,
                          value: newValue,
                          defaultValue: false,
                          default_order: button.default_order,
                          role: 'any',
                        })
                      }
                    }}
                  >
                    {button.label}
                  </Button>
                ))}
            </Row>
          </Col>
        </Row>
      }
    </div>
  )
}

export default QuickFilterPresets
